import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Users } from "lucide-react";
import { api } from "@/lib/api";
import { ENVIRONMENTS, ENV_ORDER, envKeyOf, tint } from "@/lib/environments";
import { SpaceAvatar } from "@/components/Avatars";
import { EnvBadge } from "@/components/EnvBadge";
import { CreateSpaceDialog } from "@/components/CreateSpaceDialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const SpaceCard = ({ ws }) => (
  <Link key={ws.id} to={`/space/${ws.id}`} data-testid={`spaces-list-card-${ws.id}`}>
    <Card className="p-5 flex items-center gap-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_24px_rgba(59,74,101,0.10)] h-full">
      <SpaceAvatar icon={ws.icon} color={ws.color} size="lg" />
      <div className="min-w-0 flex-1">
        <p className="font-heading font-semibold truncate">{ws.name}</p>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <EnvBadge ws={ws} />
          <Badge variant="secondary" className="font-normal">{ws.my_role_label}</Badge>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Users className="h-3 w-3" /> {ws.member_count}
          </span>
        </div>
      </div>
    </Card>
  </Link>
);

const EnvHeader = ({ envKey, count }) => {
  const env = ENVIRONMENTS[envKey];
  return (
    <div className="flex items-center gap-3" data-testid={`spaces-env-section-${envKey}`}>
      <span className="h-10 w-10 rounded-2xl flex items-center justify-center text-xl" style={{ backgroundColor: tint(env.color, 0.2) }} aria-hidden>
        {env.emoji}
      </span>
      <div>
        <h2 className="font-heading font-bold text-lg leading-tight" style={{ color: env.text }}>{env.label}</h2>
        <p className="text-xs text-muted-foreground">{count} {count === 1 ? "Espacio" : "Espacios"} · {env.desc}</p>
      </div>
    </div>
  );
};

export default function SpacesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const activeEnv = params.get("env") || "all";
  const { data: workspaces = [] } = useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => (await api.get("/workspaces")).data,
  });

  const groups = ENV_ORDER
    .map((key) => ({ key, items: workspaces.filter((w) => envKeyOf(w) === key) }))
    .filter((g) => g.items.length > 0);

  const setEnv = (key) => setParams(key === "all" ? {} : { env: key });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 space-y-8" data-testid="spaces-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Mis Espacios</h1>
        <Button size="sm" data-testid="spaces-create-btn" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Nuevo
        </Button>
      </div>

      {/* Pestañas de entorno */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="spaces-env-tabs">
        <button
          data-testid="spaces-env-tab-all"
          onClick={() => setEnv("all")}
          className={`px-4 h-9 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
            activeEnv === "all" ? "bg-foreground text-background" : "bg-card text-muted-foreground hover:text-foreground shadow-sm"
          }`}
        >
          Todos
        </button>
        {groups.map(({ key, items }) => {
          const env = ENVIRONMENTS[key];
          const active = activeEnv === key;
          return (
            <button
              key={key}
              data-testid={`spaces-env-tab-${key}`}
              onClick={() => setEnv(key)}
              className="px-4 h-9 rounded-full text-sm font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5"
              style={active
                ? { backgroundColor: env.color, color: "#FFFFFF" }
                : { backgroundColor: tint(env.color, 0.14), color: env.text }}
            >
              <span aria-hidden>{env.emoji}</span> {env.label}
              <span className={`text-[11px] rounded-full px-1.5 ${active ? "bg-white/25" : "bg-white/60"}`}>{items.length}</span>
            </button>
          );
        })}
      </div>

      {workspaces.length === 0 && (
        <p className="text-muted-foreground text-sm">Aún no tienes Espacios.</p>
      )}

      {(activeEnv === "all" ? groups : groups.filter((g) => g.key === activeEnv)).map(({ key, items }) => (
        <section key={key} className="space-y-4 animate-fade-up">
          <EnvHeader envKey={key} count={items.length} />
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((ws) => <SpaceCard key={ws.id} ws={ws} />)}
          </div>
        </section>
      ))}

      <CreateSpaceDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
